import { Formik } from "formik";
import { ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import * as yup from "yup";

import { PRODUCT_NAME } from "@src/shared/brand";
import { FormikInput } from "@src/shared/components/forms";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Checkbox } from "@src/shared/components/ui/checkbox";
import { Label } from "@src/shared/components/ui/label";
import { Spinner } from "@src/shared/components/ui/spinner";
import { navigationV2 } from "@src/shared/navigation/v2";

import type {
  TwoFactorChallengeValues,
  TwoFactorMethod,
} from "../../../../domain/v2";

export type { TwoFactorChallengeValues };

export interface TwoFactorPageV2Props {
  method: TwoFactorMethod;
  /**
   * Trusting a device is backed by a cookie the bearer surfaces never return, so
   * the option is hidden there rather than shown as a toggle that does nothing.
   * Required rather than defaulted: defaulting a safety-relevant flag to the
   * permissive value would let a new call site silently bring the dead toggle
   * back.
   */
  canTrustDevice: boolean;
  emailCodeSent?: boolean;
  error?: string | null;
  isSendingEmailCode?: boolean;
  isSubmitting?: boolean;
  onMethodChange: (method: TwoFactorMethod) => void;
  onSendEmailCode: () => void | Promise<void>;
  onSubmit: (values: TwoFactorChallengeValues) => void | Promise<void>;
}

const TOTP_CODE_LENGTH = 6;

const copyByMethod: Record<
  TwoFactorMethod,
  { description: string; label: string; hint: string }
> = {
  totp: {
    description: `Enter the six-digit code from the authenticator app you set up for ${PRODUCT_NAME.singular}.`,
    hint: "6-digit code",
    label: "Authenticator code",
  },
  otp: {
    description: "Enter the six-digit code we emailed you.",
    hint: "6-digit code",
    label: "Emailed code",
  },
  backup_code: {
    description:
      "Enter one of the backup codes you saved when you turned on two-factor authentication. Each code works once.",
    hint: "Backup codes are single-use",
    label: "Backup code",
  },
};

function getValidationSchema(method: TwoFactorMethod) {
  return yup.object<TwoFactorChallengeValues>({
    code:
      method === "backup_code"
        ? yup.string().trim().required("Enter a backup code.")
        : yup
            .string()
            .trim()
            .matches(
              new RegExp(`^\\d{${TOTP_CODE_LENGTH}}$`),
              `Enter all ${TOTP_CODE_LENGTH} digits.`,
            )
            .required("Enter your verification code."),
    trustDevice: yup.boolean().required(),
  });
}

export function TwoFactorPageV2({
  method,
  canTrustDevice,
  emailCodeSent = false,
  error = null,
  isSendingEmailCode = false,
  isSubmitting = false,
  onMethodChange,
  onSendEmailCode,
  onSubmit,
}: TwoFactorPageV2Props) {
  const copy = copyByMethod[method];

  return (
    <VerticalFlexbox
      as="main"
      className="mx-auto w-full max-w-[420px] text-left"
      gap="none"
    >
      <HorizontalFlexbox alignItems="center" gap="sm">
        <ShieldCheck aria-hidden="true" className="size-5 text-primary" />
        <HeaderText tag="h1" className="text-[22px] tracking-tight">
          Two-step verification
        </HeaderText>
      </HorizontalFlexbox>

      <Text appearance="subdued" className="mt-2" sizing="sm">
        {copy.description}
      </Text>

      <Formik<TwoFactorChallengeValues>
        initialValues={{ code: "", trustDevice: false }}
        validationSchema={getValidationSchema(method)}
        onSubmit={async (values) => {
          await onSubmit({ ...values, code: values.code.trim() });
        }}
      >
        {({ handleSubmit, resetForm, setFieldValue, values }) => {
          /**
           * A code typed for one method is meaningless under another, and its
           * validation error would linger, so drop it whenever the method
           * changes. Everything else in the form is preserved.
           */
          const clearCode = () => {
            resetForm({ values: { ...values, code: "" } });
          };

          const switchMethod = (next: TwoFactorMethod) => {
            clearCode();
            onMethodChange(next);
          };

          return (
            <VerticalFlexbox
              as="form"
              aria-label="Two-step verification"
              className="mt-6"
              gap="md"
              onSubmit={handleSubmit}
            >
              {emailCodeSent ? (
                <Text className="text-primary" role="status" sizing="sm">
                  We emailed you a code. It expires shortly, so use it soon.
                </Text>
              ) : null}

              <FormikInput
                autoComplete="one-time-code"
                disabled={isSubmitting}
                hint={copy.hint}
                inputMode={method === "backup_code" ? "text" : "numeric"}
                label={copy.label}
                maxLength={
                  method === "backup_code" ? undefined : TOTP_CODE_LENGTH
                }
                name="code"
                required
              />

              {canTrustDevice ? (
                <HorizontalFlexbox alignItems="center" gap="sm">
                  <Checkbox
                    checked={values.trustDevice}
                    disabled={isSubmitting}
                    id="two-factor-trust-device"
                    onCheckedChange={(checked) => {
                      void setFieldValue("trustDevice", checked === true);
                    }}
                  />
                  <Label
                    className="text-sm font-normal text-muted-foreground"
                    htmlFor="two-factor-trust-device"
                  >
                    Trust this device for 30 days
                  </Label>
                </HorizontalFlexbox>
              ) : null}

              <ErrorMessage error={error} variant="boxed" />

              <Button disabled={isSubmitting} type="submit">
                {isSubmitting ? <Spinner className="mr-1" size="sm" /> : null}
                {isSubmitting ? "Verifying..." : "Verify"}
              </Button>

              <VerticalFlexbox
                aria-label="Other ways to verify"
                as="nav"
                className="border-t border-border pt-4"
                gap="xs"
              >
                {/* Each method offers the other two, so no choice is a dead end.
                  On the emailed-code screen this stays put as a resend: better-auth
                  answers 200 even when delivery fails, so a code that never
                  arrives must not leave the user stranded. */}
                <Button
                  className="justify-start px-0"
                  disabled={isSendingEmailCode || isSubmitting}
                  onClick={() => {
                    // Resolves to the emailed-code method, so clear alongside it.
                    clearCode();
                    void onSendEmailCode();
                  }}
                  type="button"
                  variant="link"
                >
                  {isSendingEmailCode
                    ? "Sending code..."
                    : method === "otp"
                      ? "Resend code"
                      : "Email me a code instead"}
                </Button>

                {method === "totp" ? null : (
                  <Button
                    className="justify-start px-0"
                    disabled={isSubmitting}
                    onClick={() => switchMethod("totp")}
                    type="button"
                    variant="link"
                  >
                    Use my authenticator app instead
                  </Button>
                )}

                {method === "backup_code" ? null : (
                  <Button
                    className="justify-start px-0"
                    disabled={isSubmitting}
                    onClick={() => switchMethod("backup_code")}
                    type="button"
                    variant="link"
                  >
                    Use a backup code
                  </Button>
                )}

                <Button asChild className="justify-start px-0" variant="link">
                  <Link to={navigationV2.login()}>Back to login</Link>
                </Button>
              </VerticalFlexbox>
            </VerticalFlexbox>
          );
        }}
      </Formik>
    </VerticalFlexbox>
  );
}
